import {
  compact,
  every,
  filter,
  find,
  forEach,
  get,
  isEmpty,
  keys,
  map,
  orderBy,
  reduce,
  sumBy,
  toLower,
  values,
  words
} from "lodash";
import moment from "moment";
import { stringifyUrl } from "query-string";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Link, useHistory } from "react-router-dom";
import { Bar, BarChart } from "recharts";
import CenteredLoading from "../../../components/CenteredLoading";
import ExternalLink from "../../../components/ExternalLink";
import FeatureExplanation from "../../../components/FeatureExplanation";
import HotspotActionsButton from "../../../components/HotspotActionsButton";
import FilterLabels from "../../../components/labels/FilterLabels";
import Labels from "../../../components/labels/Labels";
import { getLabelColorStyle } from "../../../components/labels/utils";
import UpgradeHotspotsModal from "../../../components/modals/UpgradeHotspotsModal";
import NotificationBar from "../../../components/NotificationBar";
import PercentageChange from "../../../components/PercentageChange";
import StatusDot from "../../../components/StatusDot";
import { useAppearance } from "../../../context/appearance";
import { useAuthentication } from "../../../context/authentication";
import { useBillingPlans } from "../../../context/billingPlans";
import { DottedMenu } from "../../../context/dropdownMenu/DropdownMenuProvider";
import { getSmartLabelFromKey } from "../../../context/filteredList/FilteredListProvider";
import { useHotspotMakers } from "../../../context/hotspotMakers";
import { useHotspots } from "../../../context/hotspots";
import { useInstalls } from "../../../context/installs";
import { useLabels } from "../../../context/labels";
import {
  LoadingKey,
  useLoading
} from "../../../context/loading/LoadingProvider";
import { useMembers } from "../../../context/members";
import { useToast } from "../../../context/toast";
import { ModalConfig } from "../../../context/toast/ToastProvider";
import { useTransmitScales } from "../../../context/transmitScales";
import { useWallets } from "../../../context/wallets";
import { useWorkspaces } from "../../../context/workspaces";
import { avatarsApi, Entity } from "../../../utils/constants";
import { downloadCsv } from "../../../utils/csv";
import {
  CURRENT_CALENDAR_MONTH_DISPLAY,
  EXPORT_DATETIME_FORMAT,
  formatDateToLocale,
  PREVIOUS_CALENDAR_MONTH_DISPLAY
} from "../../../utils/dates";
import {
  ClassificationValue,
  formatHotspotName,
  formatHotspotPlace,
  getClassification
} from "../../../utils/hotspotUtils";
import { PhpHotspot, PlanType } from "../../../utils/types";
import useDataPeriod from "../../../utils/useDataPeriod";
import useDebounce from "../../../utils/useDebounce";
import useFilteredLabels, {
  getFilteredLabels,
  LabelFilterKey
} from "../../../utils/useFilteredLabels";
import { useHntMarket } from "../../../utils/useHntMarket";
import useMapConfig from "../../../utils/useMapConfig";
import useTransmitScaleConfig from "../../../utils/useTransmitScaleConfig";
import EntityDataTable from "../common/EntityDataTable";
import TablePropertiesSidebar from "../common/TablePropertiesSidebar";
import TableSearchInput from "../common/TableSearchInput";
import useDataTable, { Property } from "../common/useDataTable";
import { PaginationKey } from "../common/usePagination";
import { isActiveInstall } from "../InstallsData/utils";
import LockedWorkspace from "../LockedWorkspace";
import LockedWorkspaceByCommunityPlan from "../LockedWorkspaceByCommunityPlan";
import LockedWorkspaceByMembers from "../LockedWorkspaceByMembers";

interface Props {}

const HotspotsData: React.FC<Props> = () => {
  const { setModalWarningConfig } = useToast();
  const { wrapActionWithAuth } = useAuthentication();
  const {
    hotspotRewardsSummary,
    hotspotWitnessesCountsMap,
    hotspotRewardsHistory,
    fetchRewardsHistory,
    workspaceHotspots,
    toggleAddHotspotSidebar,
    removeAllHotspots,
    getRewardsHistoryChartData,
    setExportHotspotRewardsSidebarConfig
  } = useHotspots();
  const {
    currentWorkspace,
    currentWorkspaceId,
    workspaces,
    currentWorkspaceIsLocked,
    currentWorkspaceAllowedForCommunityPlan
  } = useWorkspaces();
  const { getHotspotMakerName } = useHotspotMakers();
  const renderDistance = useMapConfig((state) => state.renderDistance);
  const { labels, linkedLabels } = useLabels();
  const filteredLabels = useFilteredLabels((state) => state.filteredLabels);
  const [showPropertiesSidebar, setShowPropertiesSidebar] = useState(false);
  const sortOrder = useDataTable((state) => state.sortOrder[Entity.hotspot]);
  const searchInput = useDataTable(
    (state) => state.searchInput[Entity.hotspot]
  );
  const debouncedSearchInput = useDebounce(searchInput, 200);
  const { displayInCurrency } = useHntMarket();
  const dataPeriod = useDataPeriod((state) => state.dataPeriod);
  const rotateDataPeriod = useDataPeriod((state) => state.rotateDataPeriod);
  const fetchedRewards = useRef(false);
  const { membersLimitExceeded } = useMembers();
  const isHistoricalValuesPropertyVisible = useDataTable((state) =>
    state.visibleProperties[Entity.hotspot].includes("historicalValues")
  );
  const toggleIsActive = useTransmitScaleConfig(
    (state) => state.toggleIsActive
  );
  const { calculatedTransmitScales, isTransmitScaleActive } =
    useTransmitScales();
  const { wallets } = useWallets();
  const { installs, toggleInstallSidebar } = useInstalls();
  const { getIsReady } = useLoading();
  const { isDark } = useAppearance();
  const { setModalUpgradeConfig } = useToast();
  const { setPaymentStep, getPlanByType, setNewPlan } = useBillingPlans();
  const history = useHistory();
  const [upgradeHotspotsConfig, setUpgradeHotspotsConfig] =
    useState<ModalConfig>({
      open: false
    });

  const getActiveInstall = useCallback(
    ({ id }: PhpHotspot) =>
      find(values(installs).filter(isActiveInstall), { hotspot_uuid: id }),
    [installs]
  );

  useEffect(() => {
    if (!currentWorkspace?.active_plan) return;

    if (currentWorkspace.active_plan?.type !== PlanType.community) {
      return;
    }

    if (
      currentWorkspace.counts?.hotspot <=
      currentWorkspace.active_plan?.allowed_hotspots
    ) {
      return;
    }

    setModalUpgradeConfig({
      open: true,
      nextPlan: getPlanByType(PlanType.pro),
      resolve: () => {
        setNewPlan((prevState) => ({
          ...prevState,
          plan: getPlanByType(PlanType.pro)
        }));
      }
    });
  }, [
    currentWorkspace?.active_plan,
    currentWorkspace?.counts?.hotspot,
    getPlanByType,
    history,
    setModalUpgradeConfig,
    setNewPlan,
    setPaymentStep
  ]);

  const properties: { [key: string]: Property<PhpHotspot> } = useMemo(
    () => ({
      wallet: {
        key: "wallet",
        title: "Wallet",
        sortIteratee: "owner",
        renderContent: (hotspot) => hotspot.owner
      },
      place: {
        key: "place",
        title: "Place",
        style: { minWidth: "18rem" },
        sortIteratee: "place",
        renderContent: (hotspot) => formatHotspotPlace(hotspot.place)
      },
      id: {
        key: "id",
        title: "ID",
        sortIteratee: "id",
        renderContent: (hotspot) => hotspot.id
      },
      name: {
        key: "name",
        title: "Hotspots",
        style: { minWidth: "18rem" },
        sortIteratee: "name",
        renderForExport: (hotspot) => formatHotspotName(hotspot),
        renderContent: (hotspot) => (
          <Link
            className="relative flex items-center space-x-3 group text-sm font-medium text-gray-900 dark:text-white"
            to={`/hotspots/${hotspot.id}/status`}
          >
            <div className="flex-shrink-0">
              <span className="inline-block relative">
                <img
                  className="h-10 w-10 rounded-full"
                  src={stringifyUrl({
                    url: `${avatarsApi}/bottts/${hotspot.id}.svg`,
                    query: { w: 40, h: 40 }
                  })}
                  alt="Hotspot avatar"
                />
                <StatusDot
                  hotspot={hotspot}
                  withoutCustomRewardScaling
                  className="absolute top-0 right-0 block"
                />
              </span>
            </div>
            <div className="flex-1 min-w-0 focus:outline-none">
              <span className="absolute inset-0" aria-hidden="true" />
              <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
                {formatHotspotName(hotspot)}
              </p>
              <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400">
                {formatHotspotPlace(hotspot?.place)}
              </p>
            </div>
          </Link>
        )
      },
      historicalValues: {
        key: "historicalValues",
        title: dataPeriod.title,
        style: { minWidth: "12rem" },
        excludeForExport: true,
        onHeaderClick: rotateDataPeriod,
        renderContent: (hotspot) => {
          if (!hotspotRewardsHistory[hotspot.id]) return null;

          return (
            sumBy(getRewardsHistoryChartData(hotspot.id), "total") > 0 && (
              <BarChart
                width={150}
                height={40}
                data={getRewardsHistoryChartData(hotspot.id)}
              >
                <Bar dataKey="total" fill="#a96ceb" isAnimationActive={false} />
              </BarChart>
            )
          );
        }
      },
      addInstall: {
        key: "addInstall",
        title: "Install",
        excludeForExport: true,
        style: { minWidth: "10rem" },
        renderContent: (hotspot) => {
          const activeInstall = getActiveInstall(hotspot);
          return activeInstall ? (
            <Link to={`/installs/${activeInstall.uuid}/info`}>
              <div
                className="inline-flex items-center rounded-full font-medium px-3 text-sm"
                style={getLabelColorStyle("#e4e669", isDark)}
              >
                active install
              </div>
            </Link>
          ) : (
            <button
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-default dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
              onClick={() =>
                toggleInstallSidebar({
                  uuid: "",
                  created_by: "",
                  created_at: "",
                  updated_by: "",
                  updated_at: "",
                  hotspot_uuid: hotspot.id,
                  location_uuid: "",
                  installed_at: "",
                  uninstalled_at: "",
                  commissions: []
                })
              }
            >
              Add install
            </button>
          );
        }
      },
      labels: {
        key: "labels",
        title: "Labels",
        style: { minWidth: "26rem" },
        renderForExport: (hotspot) => {
          const hotspotLabelNames = compact(
            (linkedLabels[hotspot.id] || []).map((uuid) => labels[uuid]?.name)
          );

          if (isEmpty(hotspotLabelNames)) return null;

          return hotspotLabelNames.join(", ");
        },
        renderContent: (hotspot) => {
          return (
            <Labels
              entity={Entity.hotspot}
              linkedUuid={hotspot.id}
              filterKey={LabelFilterKey.workspaceHotspots}
              contextWalletAddresses={[hotspot.owner]}
            />
          );
        }
      },
      messages: {
        key: "total_messages",
        title: "Messages",
        sortIteratee: "total_messages",
        renderContent: (hotspot) => hotspot.total_messages
      },
      day: {
        key: "day",
        title: "24h",
        sortIteratee: "rewardsSummary.day",
        renderContent: (hotspot) =>
          displayInCurrency(hotspotRewardsSummary[hotspot.id]?.day, {
            needsDivision: true
          })
      },
      dayWeekAverage: {
        key: "dayWeekAverage",
        title: "24h wk avg",
        style: { minWidth: "10rem" },
        sortIteratee: "rewardsSummary.dayWeekAverage",
        renderContent: (hotspot) =>
          displayInCurrency(hotspotRewardsSummary[hotspot.id]?.dayWeekAverage, {
            needsDivision: true
          })
      },
      dayPercentageChangeToDayWeekAverage: {
        key: "dayPercentageChangeToDayWeekAverage",
        title: "24h %Δ wk avg",
        style: { minWidth: "12rem" },
        sortIteratee: "rewardsSummary.dayPercentageChangeToDayWeekAverage",
        renderForExport: (hotspot) => {
          const value =
            hotspotRewardsSummary[hotspot.id]
              ?.dayPercentageChangeToDayWeekAverage;
          if (value == null) return null;
          return value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        },
        renderContent: (hotspot) => (
          <PercentageChange
            percentageChange={
              hotspotRewardsSummary[hotspot.id]
                ?.dayPercentageChangeToDayWeekAverage
            }
          />
        )
      },
      week: {
        key: "week",
        title: "7d",
        style: { minWidth: "9rem" },
        sortIteratee: "rewardsSummary.week",
        renderContent: (hotspot) =>
          displayInCurrency(hotspotRewardsSummary[hotspot.id]?.week, {
            needsDivision: true
          })
      },
      previousWeek: {
        key: "previousWeek",
        title: "Prev 7d",
        style: { minWidth: "8rem" },
        sortIteratee: "rewardsSummary.previousWeek",
        renderContent: (hotspot) =>
          displayInCurrency(hotspotRewardsSummary[hotspot.id]?.previousWeek, {
            needsDivision: true
          })
      },
      weekPercentageChange: {
        key: "weekPercentageChange",
        title: "7d %Δ",
        sortIteratee: "rewardsSummary.weekPercentageChange",
        renderForExport: (hotspot) => {
          const value = hotspotRewardsSummary[hotspot.id]?.weekPercentageChange;
          if (value == null) return null;
          return value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        },
        renderContent: (hotspot) => (
          <PercentageChange
            percentageChange={
              hotspotRewardsSummary[hotspot.id]?.weekPercentageChange
            }
          />
        )
      },
      month: {
        key: "month",
        title: "30d",
        sortIteratee: "rewardsSummary.month",
        renderContent: (hotspot) =>
          displayInCurrency(hotspotRewardsSummary[hotspot.id]?.month, {
            needsDivision: true
          })
      },
      previousMonth: {
        key: "previousMonth",
        title: "Prev 30d",
        style: { minWidth: "9rem" },
        sortIteratee: "rewardsSummary.previousMonth",
        renderContent: (hotspot) =>
          displayInCurrency(hotspotRewardsSummary[hotspot.id]?.previousMonth, {
            needsDivision: true
          })
      },
      monthPercentageChange: {
        key: "monthPercentageChange",
        title: "30d %Δ",
        style: { minWidth: "9rem" },
        sortIteratee: "rewardsSummary.monthPercentageChange",
        renderForExport: (hotspot) => {
          const value =
            hotspotRewardsSummary[hotspot.id]?.monthPercentageChange;
          if (value == null) return null;
          return value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        },
        renderContent: (hotspot) => (
          <PercentageChange
            percentageChange={
              hotspotRewardsSummary[hotspot.id]?.monthPercentageChange
            }
          />
        )
      },
      currentCalendarMonth: {
        key: "currentCalendarMonth",
        title: CURRENT_CALENDAR_MONTH_DISPLAY,
        style: { minWidth: "12rem" },
        sortIteratee: "rewardsSummary.currentCalendarMonth",
        renderContent: (hotspot) =>
          displayInCurrency(
            hotspotRewardsSummary[hotspot.id]?.currentCalendarMonth,
            {
              needsDivision: true
            }
          )
      },
      previousCalendarMonth: {
        key: "previousCalendarMonth",
        title: PREVIOUS_CALENDAR_MONTH_DISPLAY,
        style: { minWidth: "12rem" },
        sortIteratee: "rewardsSummary.previousCalendarMonth",
        renderContent: (hotspot) =>
          displayInCurrency(
            hotspotRewardsSummary[hotspot.id]?.previousCalendarMonth,
            {
              needsDivision: true
            }
          )
      },
      lifetime: {
        key: "lifetime",
        title: "Lifetime",
        sortIteratee: "rewardsSummary.lifetime",
        renderContent: (hotspot) =>
          displayInCurrency(hotspotRewardsSummary[hotspot.id]?.lifetime, {
            needsDivision: true
          })
      },
      allWitnessedBy: {
        key: "allWitnessedBy",
        title: "Witnessed by (all)",
        style: { minWidth: "11rem" },
        sortIteratee: "witnessesSummary.witnessedBy.all",
        renderContent: (hotspot) =>
          hotspotWitnessesCountsMap[hotspot.id]?.witnessedBy.all
      },
      validWitnessedBy: {
        key: "validWitnessedBy",
        title: "Witnessed by (valid)",
        style: { minWidth: "11rem" },
        sortIteratee: "witnessesSummary.witnessedBy.valid",
        renderContent: (hotspot) =>
          hotspotWitnessesCountsMap[hotspot.id]?.witnessedBy.valid
      },
      invalidWitnessedBy: {
        key: "invalidWitnessedBy",
        title: "Witnessed by (invalid)",
        style: { minWidth: "11rem" },
        sortIteratee: "witnessesSummary.witnessedBy.invalid",
        renderContent: (hotspot) =>
          hotspotWitnessesCountsMap[hotspot.id]?.witnessedBy.invalid
      },
      allWitnessedOthers: {
        key: "allWitnessedOthers",
        title: "Witnessed (all)",
        sortIteratee: "witnessesSummary.witnessedOthers.all",
        renderContent: (hotspot) =>
          hotspotWitnessesCountsMap[hotspot.id]?.witnessedOthers.all
      },
      validWitnessedOthers: {
        key: "validWitnessedOthers",
        title: "Witnessed (valid)",
        sortIteratee: "witnessesSummary.witnessedOthers.valid",
        renderContent: (hotspot) =>
          hotspotWitnessesCountsMap[hotspot.id]?.witnessedOthers.valid
      },
      invalidWitnessedOthers: {
        key: "invalidWitnessedOthers",
        title: "Witnessed (invalid)",
        sortIteratee: "witnessesSummary.witnessedOthers.invalid",
        renderContent: (hotspot) =>
          hotspotWitnessesCountsMap[hotspot.id]?.witnessedOthers.invalid
      },
      allBidirectional: {
        key: "allBidirectional",
        title: "Bidirectional witnesses (all)",
        style: { minWidth: "15rem" },
        sortIteratee: "witnessesSummary.bidirectional.all",
        renderContent: (hotspot) =>
          hotspotWitnessesCountsMap[hotspot.id]?.bidirectional.all
      },
      validBidirectional: {
        key: "validBidirectional",
        title: "Bidirectional witnesses (valid)",
        style: { minWidth: "15rem" },
        sortIteratee: "witnessesSummary.bidirectional.valid",
        renderContent: (hotspot) =>
          hotspotWitnessesCountsMap[hotspot.id]?.bidirectional.valid
      },
      invalidBidirectional: {
        key: "invalidBidirectional",
        title: "Bidirectional witnesses (invalid)",
        style: { minWidth: "15rem" },
        sortIteratee: "witnessesSummary.bidirectional.invalid",
        renderContent: (hotspot) =>
          hotspotWitnessesCountsMap[hotspot.id]?.bidirectional.invalid
      },
      activated: {
        key: "activated",
        title: "Simulation activated",
        renderForExport: (hotspot) =>
          isTransmitScaleActive(hotspot.id) ? "Yes" : "No",
        renderContent: (hotspot) => {
          const isActive = isTransmitScaleActive(hotspot.id);

          return (
            <div className="flex items-center">
              <button
                type="button"
                className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none ${
                  isActive
                    ? "bg-hotspotty-400 dark:bg-gray-100 dark:hover:bg-gray-200"
                    : "bg-gray-200 dark:bg-gray-500 dark:hover:bg-gray-400"
                }`}
                aria-pressed="false"
                aria-labelledby="toggle-interactive"
                onClick={() => toggleIsActive(hotspot.id)}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white dark:bg-gray-800 shadow transform ring-0 transition ease-in-out duration-200 ${
                    isActive ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          );
        }
      },
      simulatedTransmitScale: {
        key: "simulatedTransmitScale",
        title: "Simulated transmit scale",
        style: { minWidth: "16rem" },
        sortIteratee: "simulatedTransmitScale",
        renderForExport: (hotspot) => {
          const transmitScale = get(calculatedTransmitScales, hotspot.id, 0);
          if (!transmitScale) return null;
          return transmitScale.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        },
        renderContent: (hotspot) => {
          const calculatedTransmitScale = get(
            calculatedTransmitScales,
            hotspot.id,
            0
          );

          if (!calculatedTransmitScale) return null;

          return <ClassificationValue value={calculatedTransmitScale} />;
        }
      },
      transmitScale: {
        key: "transmitScale",
        title: "Current transmit scale",
        style: { minWidth: "15rem" },
        sortIteratee: "reward_scale",
        renderForExport: (hotspot) => {
          if (!hotspot.reward_scale) return null;
          return hotspot.reward_scale.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        },
        renderContent: (hotspot) => {
          if (!hotspot.reward_scale) return null;
          return <ClassificationValue value={hotspot.reward_scale} />;
        }
      },
      witnessRewardUnits: {
        key: "witnessRewardUnits",
        title: "Witness reward units",
        style: { minWidth: "15rem" },
        sortIteratee: "rx_reward_unit",
        renderForExport: (hotspot) => {
          return hotspot.rx_reward_unit.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        },
        renderContent: (hotspot) => {
          return (
            <ClassificationValue
              value={hotspot.rx_reward_unit}
              withoutTransmitScaleTooltip
            />
          );
        }
      },
      placement: {
        key: "placement",
        title: "Placement",
        style: { minWidth: "16rem" },
        sortIteratee: "place",
        renderForExport: (hotspot) => formatHotspotPlace(hotspot.place),
        renderContent: ({ place, lat, lng }) => (
          <>
            {formatHotspotPlace(place)}
            {!!lat && !!lng && (
              <ExternalLink
                text="Google Earth"
                url={`https://earth.google.com/web/search/${lat},${lng}/`}
              />
            )}
          </>
        )
      },
      timestampAdded: {
        key: "timestampAdded",
        title: "Date added",
        style: { minWidth: "10rem" },
        sortIteratee: "timestamp_added",
        renderForExport: (hotspot) =>
          moment
            .unix(hotspot.timestamp_added)
            .utc()
            .format(EXPORT_DATETIME_FORMAT),
        renderContent: (hotspot) =>
          formatDateToLocale(
            moment.unix(hotspot.timestamp_added).utc().toLocaleString()
          )
      },
      online: {
        key: "online",
        title: "Active",
        sortIteratee: "online",
        renderContent: (hotspot) => (hotspot.online ? "Yes" : "No")
      },
      lastUpdate: {
        key: "lastUpdate",
        title: "Last updated",
        style: { minWidth: "10rem" },
        sortIteratee: "last_update",
        renderForExport: (hotspot) =>
          moment.unix(hotspot.last_update).utc().format(EXPORT_DATETIME_FORMAT),
        renderContent: (hotspot) =>
          moment.unix(hotspot.last_update).utc().fromNow()
      },
      lastAsserted: {
        key: "lastAsserted",
        title: "Last location assertion",
        style: { minWidth: "16rem" },
        sortIteratee: "last_assertion",
        renderForExport: (hotspot) => {
          const value = hotspot.last_assertion;
          if (!value) return null;
          return moment.unix(value).utc().format(EXPORT_DATETIME_FORMAT);
        },
        renderContent: (hotspot) =>
          hotspot.last_assertion
            ? moment.unix(hotspot.last_assertion).utc().fromNow()
            : "Never"
      },
      elevation: {
        key: "elevation",
        title: "Installation height",
        style: { minWidth: "14rem" },
        sortIteratee: "elevation",
        renderForExport: (hotspot) => hotspot.elevation ?? null,
        renderContent: (hotspot) =>
          hotspot.elevation ? renderDistance(hotspot.elevation, false) : "-"
      },
      gain: {
        key: "gain",
        title: "Antenna gain",
        style: { minWidth: "10rem" },
        sortIteratee: "gain",
        renderForExport: (hotspot) => (hotspot.gain ? hotspot.gain / 10 : null),
        renderContent: (hotspot) =>
          hotspot.gain ? `${hotspot.gain / 10}dBi` : "-"
      },
      maker: {
        key: "maker",
        title: "Maker",
        style: { minWidth: "15rem" },
        sortIteratee: "maker",
        renderForExport: (hotspot) => getHotspotMakerName(hotspot.payer),
        renderContent: (hotspot) => (
          <Link
            className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
            to={`/hotspots/makers/${hotspot.payer}`}
          >
            {getHotspotMakerName(hotspot.payer)}
          </Link>
        )
      },
      actions: {
        key: "actions",
        title: "Actions",
        excludeForExport: true,
        renderContent: (hotspot) => (
          <HotspotActionsButton
            hotspot={hotspot}
            toggleAddInstallForm={() =>
              toggleInstallSidebar({
                uuid: "",
                created_by: "",
                created_at: "",
                updated_by: "",
                updated_at: "",
                hotspot_uuid: hotspot.id,
                location_uuid: "",
                installed_at: "",
                uninstalled_at: "",
                commissions: []
              })
            }
          />
        )
      }
    }),
    [
      dataPeriod.title,
      rotateDataPeriod,
      hotspotRewardsHistory,
      getRewardsHistoryChartData,
      getActiveInstall,
      isDark,
      toggleInstallSidebar,
      linkedLabels,
      labels,
      displayInCurrency,
      hotspotRewardsSummary,
      hotspotWitnessesCountsMap,
      isTransmitScaleActive,
      toggleIsActive,
      calculatedTransmitScales,
      renderDistance,
      getHotspotMakerName
    ]
  );

  const smartLabelFilters: {
    [key: string]: (hotspot: PhpHotspot) => boolean;
  } = useMemo(
    () => ({
      active: ({ online }) => online,
      inactive: ({ online }) => !online,
      "active install": (hotspot) => !!getActiveInstall(hotspot),
      "no active install": (hotspot) => !getActiveInstall(hotspot),
      activated: ({ id }) => isTransmitScaleActive(id),
      denylist: ({ deny_list }) => deny_list,
      ...reduce(
        [
          "excellent",
          "good",
          "mediocre",
          "poor",
          "terrible",
          "inactive",
          "disabled"
        ],
        (
          acc: {
            [key: string]: (hotspot: PhpHotspot) => boolean;
          },
          classification
        ) => {
          acc[`${classification} transmit scale`] = ({ id }) => {
            const isActive = isTransmitScaleActive(id);
            const locationClassification = isActive
              ? getClassification(get(calculatedTransmitScales, id, 0))
              : "disabled";
            return classification === locationClassification;
          };

          return acc;
        },
        {}
      )
    }),
    [getActiveInstall, isTransmitScaleActive, calculatedTransmitScales]
  );

  const filteredProperties = useDataTable(
    useCallback(
      (state) =>
        state.orderedProperties[Entity.hotspot]
          .filter((key) =>
            state.visibleProperties[Entity.hotspot].includes(key)
          )
          .map((key) => properties[key]),
      [properties]
    )
  );

  const allFilteredLabels = useMemo(
    () =>
      getFilteredLabels(
        filteredLabels[LabelFilterKey.workspaceHotspots],
        labels,
        smartLabelFilters,
        Entity.hotspot
      ),
    [filteredLabels, labels, smartLabelFilters]
  );

  useEffect(() => {
    if (
      !fetchedRewards.current &&
      !isEmpty(workspaceHotspots) &&
      isHistoricalValuesPropertyVisible
    ) {
      fetchRewardsHistory(map(workspaceHotspots, "id"), {
        historyRewardsWithoutToday: false,
        singleHotspot: false
      });
      fetchedRewards.current = true;
    }
  }, [
    fetchRewardsHistory,
    isHistoricalValuesPropertyVisible,
    workspaceHotspots
  ]);

  const filteredHotspots = useMemo(() => {
    const filteredHotspotLabels = filter(allFilteredLabels, {
      type: Entity.hotspot
    });
    const filteredWalletLabels = filter(allFilteredLabels, {
      type: Entity.wallet
    });

    const filteredWalletUuids: string[] = [];

    forEach(linkedLabels, (linkedLabelUuids, walletUuid) => {
      filteredWalletLabels.forEach(({ uuid }) => {
        if (linkedLabelUuids.includes(uuid)) {
          filteredWalletUuids.push(walletUuid);
        }
      });
    });

    const filteredWalletAddresses = compact(
      filteredWalletUuids.map((walletUuid) => wallets[walletUuid]?.address)
    );

    let filteredList = workspaceHotspots.filter((hotspot) => {
      if (
        !isEmpty(filteredHotspotLabels) &&
        !every(filteredHotspotLabels, ({ uuid }) => {
          const smartFilterFunction = (smartLabelFilters as any)[uuid]; // TODO: fix typing

          if (uuid === "denylist" && hotspot.deny_list) {
            return true;
          }

          return smartFilterFunction
            ? smartFilterFunction(hotspot)
            : (linkedLabels[hotspot.id] || []).includes(uuid);
        })
      ) {
        return false;
      }

      if (
        !isEmpty(filteredWalletAddresses) &&
        !filteredWalletAddresses.includes(hotspot.owner)
      ) {
        return false;
      }

      if (debouncedSearchInput) {
        const searchInputWords = words(debouncedSearchInput, /[^, ]+/g).map(
          toLower
        );

        return every(
          searchInputWords,
          (word) =>
            hotspot.name?.includes(word) ||
            toLower(formatHotspotPlace(hotspot.place)).includes(word) ||
            toLower(hotspot.id).includes(word) ||
            toLower(hotspot.owner).includes(word)
        );
      }

      return true;
    });

    if (sortOrder) {
      const iterateeFields = sortOrder.iteratee.split(".");

      type Iteratee = string | ((hotspot: PhpHotspot) => number | string);
      let iteratee: Iteratee = sortOrder.iteratee;

      if (sortOrder.iteratee === "simulatedTransmitScale") {
        iteratee = (hotspot) => get(calculatedTransmitScales, hotspot.id, 0);
      }

      if (sortOrder.iteratee === "maker") {
        iteratee = (hotspot: PhpHotspot) => getHotspotMakerName(hotspot.payer);
      }

      if (iterateeFields.length > 1) {
        const [type, ...otherIterateeFields] = iterateeFields;

        switch (type) {
          case "rewardsSummary":
            iteratee = (hotspot) =>
              get(hotspotRewardsSummary, [
                hotspot.id,
                ...otherIterateeFields
              ]) || 0;
            break;
          case "witnessesSummary":
            iteratee = (hotspot) =>
              get(hotspotWitnessesCountsMap, [
                hotspot.id,
                ...otherIterateeFields
              ]) || 0;
            break;
        }
      }

      filteredList = orderBy(filteredList, [iteratee], [sortOrder.order]);
    }

    if (currentWorkspace?.active_plan?.allowed_hotspots === 0) return [];

    if (currentWorkspace?.active_plan?.allowed_hotspots) {
      return filteredList.slice(-currentWorkspace.active_plan.allowed_hotspots);
    }

    return filteredList;
  }, [
    allFilteredLabels,
    linkedLabels,
    workspaceHotspots,
    sortOrder,
    currentWorkspace,
    wallets,
    debouncedSearchInput,
    smartLabelFilters,
    calculatedTransmitScales,
    getHotspotMakerName,
    hotspotRewardsSummary,
    hotspotWitnessesCountsMap
  ]);

  const isLoading = useMemo(
    () =>
      isEmpty(workspaceHotspots) && !getIsReady([LoadingKey.workspaceHotspots]),
    [getIsReady, workspaceHotspots]
  );

  const getCsvData = useCallback(
    (hotspotsToExport: PhpHotspot[]) =>
      hotspotsToExport.map((hotspot) =>
        filteredProperties
          .filter((property) => !property.excludeForExport)
          .reduce((acc: { [propertyKey: string]: any }, property) => {
            if (typeof property.title !== "string") return acc;

            acc[property.title] = property.renderForExport
              ? property.renderForExport(hotspot)
              : property.renderContent(hotspot);
            return acc;
          }, {})
      ),
    [filteredProperties]
  );

  const renderPageContent = useMemo(() => {
    if (currentWorkspaceIsLocked) {
      return (
        <LockedWorkspace
          title="Payment failed"
          paragraphs={[
            "Your subscription payment failed. Please resolve the issue with your payment method."
          ]}
          buttonText="Update payment details"
          buttonLink="/workspaces/settings/billing"
        />
      );
    }

    if (!currentWorkspaceAllowedForCommunityPlan) {
      return <LockedWorkspaceByCommunityPlan />;
    }

    if (membersLimitExceeded) {
      return <LockedWorkspaceByMembers />;
    }

    if (isLoading) {
      return <CenteredLoading className="sm:h-full h-96" />;
    }

    if (isEmpty(workspaceHotspots)) {
      return (
        <FeatureExplanation
          title="Hotspots"
          paragraphs={[
            "Add hotspots to manage their individual and combined performance.",
            <>
              We recommend adding your{" "}
              <Link
                className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
                to="/workspace/wallets"
              >
                wallet
              </Link>
              , which automatically tracks its hotspots even if they are added
              afterwards.
            </>,
            "Nevertheless, tracking individual hotspots is also supported."
          ]}
        >
          <button
            id="add-hotspot-button"
            type="button"
            className="mt-6 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
            onClick={wrapActionWithAuth(() => toggleAddHotspotSidebar())}
          >
            Add hotspot
          </button>
        </FeatureExplanation>
      );
    }

    return (
      <>
        {currentWorkspace &&
          currentWorkspace?.counts?.hotspot >
            currentWorkspace.active_plan?.allowed_hotspots && (
            <NotificationBar
              linkTitle="Increase hotspot limit"
              onClick={() => {
                if (currentWorkspace.active_plan?.type !== PlanType.community) {
                  setNewPlan((prevState) => ({
                    ...prevState,
                    hotspotAmount: currentWorkspace?.counts.hotspot
                  }));
                  setUpgradeHotspotsConfig({ open: true });

                  return;
                }

                history.push("/pricing");
              }}
              content={`This workspace has a limit of ${currentWorkspace?.active_plan?.allowed_hotspots} hotspots and your workspace has ${currentWorkspace?.counts?.hotspot}.`}
            />
          )}

        <UpgradeHotspotsModal
          config={upgradeHotspotsConfig}
          setConfig={setUpgradeHotspotsConfig}
        />

        <div className="p-4">
          <TableSearchInput
            entity={Entity.hotspot}
            placeholder="Search hotspots by name, city or country"
          />

          <div className="mt-2 flex items-center">
            <FilterLabels
              className="flex-1"
              entity={Entity.hotspot}
              filteredLabels={allFilteredLabels}
              smartLabels={keys(smartLabelFilters).map((key) =>
                getSmartLabelFromKey(key, Entity.hotspot)
              )}
              filterKey={LabelFilterKey.workspaceHotspots}
            />

            <DottedMenu
              text="Open options"
              menuItems={[
                {
                  text: "Edit columns",
                  onClick: () => {
                    setShowPropertiesSidebar(true);
                  }
                },
                {
                  onClick: wrapActionWithAuth(() => {
                    downloadCsv(
                      getCsvData(filteredHotspots),
                      `${
                        workspaces[currentWorkspaceId ?? ""]?.name ?? ""
                      } hotspots ${moment().utc().format("YYYY_MM_DD_HH_mm")}`
                    );
                  }),
                  text: `Export ${
                    filteredHotspots.length !== workspaceHotspots.length
                      ? "filtered"
                      : "all"
                  } hotspots`,
                  disabled: isEmpty(filteredHotspots)
                },
                {
                  onClick: wrapActionWithAuth(() => {
                    if (
                      !currentWorkspace?.permissions["export-hotspot-rewards"]
                    ) {
                      setModalUpgradeConfig({
                        open: true,
                        resolve: () => {
                          setNewPlan((prevState) => ({
                            ...prevState,
                            plan: getPlanByType(PlanType.pro)
                          }));
                        },
                        nextPlan: getPlanByType(PlanType.pro),
                        featureTitle: "Export Hotspot rewards",
                        featureDescription:
                          "This feature is only available in workspaces with a pro plan or higher. Upgrade your plan to unlock this feature."
                      });

                      return;
                    }

                    setExportHotspotRewardsSidebarConfig({
                      show: true,
                      hotspotIds: filteredHotspots.map((hotspot) => hotspot.id)
                    });
                  }),
                  text: "Export all hotspot rewards",
                  disabled: isEmpty(filteredHotspots)
                },
                {
                  disabled: isEmpty(filteredHotspots),
                  onClick: () => {
                    filteredHotspots.forEach((hotspot) => {
                      if (!isTransmitScaleActive(hotspot.id)) {
                        toggleIsActive(hotspot.id);
                      }
                    });
                  },
                  text: `Activate ${
                    allFilteredLabels.length ? "filtered" : "all"
                  } hotspots`
                },
                {
                  onClick: () => {
                    filteredHotspots.forEach((hotspot) => {
                      if (isTransmitScaleActive(hotspot.id)) {
                        toggleIsActive(hotspot.id);
                      }
                    });
                  },
                  text: `Deactivate ${
                    allFilteredLabels.length ? "filtered" : "all"
                  } hotspots`,
                  disabled: isEmpty(filteredHotspots)
                },
                {
                  onClick: wrapActionWithAuth(() => {
                    setModalWarningConfig({
                      open: true,
                      resolve: () => {
                        removeAllHotspots();
                      }
                    });
                  }),
                  text: "Remove all hotspots",
                  disabled:
                    isEmpty(workspaceHotspots) ||
                    workspaceHotspots.length !== filteredHotspots.length
                }
              ]}
            />

            <button
              id="add-hotspot-button"
              type="button"
              className="ml-4 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
              onClick={wrapActionWithAuth(() => toggleAddHotspotSidebar())}
            >
              Add hotspot
            </button>
          </div>
        </div>

        <EntityDataTable<PhpHotspot>
          paginationKey={PaginationKey.workspaceHotspots}
          entity={Entity.hotspot}
          items={filteredHotspots}
          properties={properties}
          selectionActions={[
            {
              title: "Export hotspots",
              callback: (selectedHotspots: PhpHotspot[]) => {
                downloadCsv(
                  getCsvData(selectedHotspots),
                  `${
                    workspaces[currentWorkspaceId ?? ""]?.name ?? ""
                  } hotspots ${moment().utc().format("YYYY_MM_DD_HH_mm")}`
                );
              }
            },
            {
              title: "Export hotspot rewards",
              callback: (selectedHotspots: PhpHotspot[]) => {
                if (!currentWorkspace?.permissions["export-hotspot-rewards"]) {
                  setModalUpgradeConfig({
                    open: true,
                    resolve: () => {
                      setNewPlan((prevState) => ({
                        ...prevState,
                        plan: getPlanByType(PlanType.pro)
                      }));
                    },
                    nextPlan: getPlanByType(PlanType.pro),
                    featureTitle: "Export Hotspot rewards",
                    featureDescription:
                      "This feature is only available in workspaces with a pro plan or higher. Upgrade your plan to unlock this feature."
                  });

                  return;
                }

                setExportHotspotRewardsSidebarConfig({
                  show: true,
                  hotspotIds: selectedHotspots.map((hotspot) => hotspot.id)
                });
              }
            },
            {
              title: "Activate hotspots",
              callback: (selectedHotspots: PhpHotspot[]) => {
                selectedHotspots.forEach((hotspot) => {
                  if (!isTransmitScaleActive(hotspot.id)) {
                    toggleIsActive(hotspot.id);
                  }
                });
              }
            },

            {
              title: "Deactivate hotspots",
              callback: (selectedHotspots: PhpHotspot[]) => {
                selectedHotspots.forEach((hotspot) => {
                  if (isTransmitScaleActive(hotspot.id)) {
                    toggleIsActive(hotspot.id);
                  }
                });
              }
            }
          ]}
        />
      </>
    );
  }, [
    allFilteredLabels,
    currentWorkspace,
    currentWorkspaceAllowedForCommunityPlan,
    currentWorkspaceId,
    currentWorkspaceIsLocked,
    filteredHotspots,
    getCsvData,
    getPlanByType,
    history,
    isLoading,
    isTransmitScaleActive,
    membersLimitExceeded,
    properties,
    removeAllHotspots,
    setExportHotspotRewardsSidebarConfig,
    setModalUpgradeConfig,
    setModalWarningConfig,
    setNewPlan,
    smartLabelFilters,
    toggleAddHotspotSidebar,
    toggleIsActive,
    upgradeHotspotsConfig,
    workspaceHotspots,
    workspaces,
    wrapActionWithAuth
  ]);

  return (
    <>
      <TablePropertiesSidebar<PhpHotspot>
        entity={Entity.hotspot}
        properties={properties}
        open={showPropertiesSidebar}
        setOpen={setShowPropertiesSidebar}
      />

      {renderPageContent}
    </>
  );
};

export default HotspotsData;
